import { ACTIVE_STATUSES, INACTIVE_STATUSES } from '~/features/vault/board-certification/constants';
import { getBoardCertActiveStatus } from '~/features/vault/board-certification/utils';
import { computeExpirationStatus, type ExpirationResult } from '../dashboardUtils';

export interface DerivedStatus {
  status: ExpirationResult;
  /** True when `status === 'active'` because the row either declared a
   *  permanent status (e.g. Lifetime Certification) OR has no expiry date
   *  on record at all — never because the expiry date happens to fall in
   *  the future. Drives the "No Expiration" badge branch in
   *  `buildExpirableCategory`: null-expiry board certs are not flagged as
   *  attention-worthy (see MCV-1251 follow-up). */
  lifetime: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function deriveBoardCertStatus(item: any): DerivedStatus {
  const statusLabel = typeof item?.status === 'object' ? item.status?.label : item?.status;

  if (!statusLabel) {
    return { status: 'unknown', lifetime: false };
  }

  const declaredStatus = getBoardCertActiveStatus(item.expiryDate, statusLabel);

  if (declaredStatus === 'Active' && ACTIVE_STATUSES.includes(statusLabel)) {
    return { status: 'active', lifetime: true };
  }
  if (declaredStatus === 'Inactive' && INACTIVE_STATUSES.includes(statusLabel)) {
    return { status: 'inactive', lifetime: false };
  }
  // Time-sensitive status + null expiry: the dashboard can't tell whether
  // the user hasn't entered a date yet or the credential genuinely has no
  // expiry on record. Per MCV-1251 follow-up, don't flag as attention-worthy
  // — render as "No Expiration" (green) so the row doesn't show up in the
  // "items needing attention" view or contribute to category warnings.
  if (declaredStatus === 'Unknown') {
    return { status: 'active', lifetime: true };
  }
  return { status: computeExpirationStatus(item.expiryDate), lifetime: false };
}
